import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import TypographyMask from '@/components/base/TypographyMask'

import { heroRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type HeroProps = HTMLAttributes<HTMLDivElement> & {
  alignment: 'left' | 'right' | 'center'
  title: string
  text: string
  primary_button: WPLink
  secondary_button: WPLink
}

const Hero = ({
  alignment,
  title,
  text,
  primary_button,
  secondary_button,
  ...props
}: HeroProps) => {
  return (
    <Motion animation={heroRevealAnimation} preventOnTouch={false}>
      <Section
        className={cn('h-lvh-vh w-full pt-[var(--header-height)] md:pt-0')}
        data-hero
        {...props}
      >
        <Container className="h-full">
          <Grid className="h-full">
            <div
              className={cn(
                'flex h-full flex-col items-start justify-center gap-spacing-lg',
                'col-span-4 md:col-span-4 lg:col-span-5',
                alignment === 'right' && 'md:col-start-3 lg:col-start-7',
                alignment === 'left' && 'lg:col-start-2',
                alignment === 'center' &&
                  'items-center text-center md:col-start-2 lg:col-span-6 lg:col-start-4'
              )}
            >
              <Typography
                variant="h2"
                component="h1"
                className="!font-light text-text-primary-dark"
                data-motion-title
              >
                {title}
              </Typography>
              <Typography
                variant="large"
                className="font-medium text-text-accent-light-dark"
                data-motion-text
              >
                {text}
              </Typography>
              <div
                className={cn(
                  'justify-left flex w-full flex-wrap gap-spacing-sm',
                  alignment === 'center' && 'justify-center'
                )}
              >
                {primary_button?.title ? (
                  <Button
                    asChild
                    className="w-full md:w-auto"
                    variant="primary"
                    data-motion-button-primary
                  >
                    <Link href={primary_button!.url} target={primary_button?.target}>
                      <TypographyMask>
                        <Typography variant="regular">{primary_button?.title}</Typography>
                      </TypographyMask>
                    </Link>
                  </Button>
                ) : null}
                {secondary_button?.title ? (
                  <Button
                    asChild
                    className="w-full md:w-auto"
                    variant="secondary"
                    endIcon={
                      <SVGIcon
                        className="h-[1.8rem] w-[1.6rem] fill-surface-primary-dark"
                        symbol="arrow-right-2"
                      />
                    }
                    data-motion-button-secondary
                  >
                    <Link href={secondary_button!.url} target={secondary_button?.target}>
                      <TypographyMask>
                        <Typography variant="regular">{secondary_button?.title}</Typography>
                      </TypographyMask>
                    </Link>
                  </Button>
                ) : null}
              </div>
            </div>
          </Grid>
        </Container>
      </Section>
    </Motion>
  )
}

export default Hero
